import { useMutation } from "@tanstack/react-query";
import { Save } from "lucide-react";
import {
  Form,
  required,
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useNotify,
  useTranslate,
} from "ra-core";
import type { FieldValues } from "react-hook-form";
import { useFormState } from "react-hook-form";
import { RecordField } from "@/components/admin/record-field";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import type { CrmDataProvider } from "../providers/types";
import { ChangePasswordDialog } from "../sales/ChangePasswordDialog";
import type { Sale } from "../types";

/**
 * "Mijn profiel": every user may change their own name and password here.
 * The e-mail address and the administrator/disabled flags are managed by an
 * administrator on the users page.
 */
export const ProfilePage = () => {
  const translate = useTranslate();
  const { identity, refetch: refetchIdentity } = useGetIdentity();
  const { data, refetch: refetchUser } = useGetOne<Sale>(
    "sales",
    { id: identity?.id as string },
    { enabled: identity?.id != null },
  );
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const { mutate } = useMutation({
    mutationKey: ["profile", identity?.id],
    mutationFn: async (values: { first_name: string; last_name: string }) => {
      if (!identity) {
        throw new Error(translate("crm.profile.record_not_found"));
      }
      // A user may only change their own name
      return dataProvider.salesUpdate(identity.id, {
        first_name: values.first_name,
        last_name: values.last_name,
      });
    },
    onSuccess: () => {
      refetchIdentity();
      refetchUser();
      notify("crm.profile.updated");
    },
    onError: () => {
      notify("crm.profile.update_error", { type: "error" });
    },
  });

  if (!identity || !data) return null;

  return (
    <div className="max-w-lg w-full mx-auto mt-8 space-y-4">
      <h1 className="text-2xl font-semibold">
        {translate("crm.profile.title")}
      </h1>
      <Card>
        <CardContent>
          <Form
            onSubmit={(values: FieldValues) =>
              mutate(values as { first_name: string; last_name: string })
            }
            record={data}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextInput
                  source="first_name"
                  label="resources.sales.fields.first_name"
                  validate={required()}
                  helperText={false}
                />
                <TextInput
                  source="last_name"
                  label="resources.sales.fields.last_name"
                  validate={required()}
                  helperText={false}
                />
              </div>
              <RecordField
                source="email"
                label="resources.sales.fields.email"
                record={data}
              />
              <div className="flex flex-row justify-between gap-2 pt-2">
                <ChangePasswordDialog />
                <ProfileSaveButton />
              </div>
            </div>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

const ProfileSaveButton = () => {
  const translate = useTranslate();
  const { isDirty, isSubmitting } = useFormState();
  return (
    <Button type="submit" disabled={!isDirty || isSubmitting}>
      <Save />
      {translate("ra.action.save")}
    </Button>
  );
};

ProfilePage.path = "/profile";
